import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { logError, scrubMessage } from "../../../../../telemetry";
// Host-agnostic despite the folder name: this is the app's only toast bridge,
// and its sink is the chat panel's <ChatToaster>, which mounts for every host.
import { emitToast } from "../../../../../../integrations/tools/outlook/toast-bridge";
import { usePptSelectionKind } from "../hooks/usePptSelectionKind";
import { useImageClassifier } from "../hooks/useImageClassifier";
import { SELECTION_CHIPS, VISIBLE_CHIP_COUNT, selectionLabel } from "../chipConfig";
import type { ActionChip } from "../types";
import styles from "./ActionChipDock.module.css";

export interface ActionChipDockProps {
  /**
   * Invoked when the user picks a chip.
   *
   * Left optional on purpose: these chips ship as placeholders (AIVA-XXXX
   * wires them up), so with no handler passed a click reports "not available
   * yet" instead of silently doing nothing. Supplying a handler is the whole
   * of the follow-up change on this component's side.
   */
  readonly onChipSelect?: (chip: ActionChip) => void | Promise<void>;
}

const CHIPS_CONNECTOR = "deckorate";

/**
 * Surface a failed chip action: a plain-language toast for the user plus a
 * scrubbed telemetry record for us. The message deliberately carries no detail
 * from the error itself, which can embed document content or host internals.
 */
function reportChipFailure(chip: ActionChip, error: unknown): void {
  logError(
    "error.connector.failure",
    {
      type: "ActionChipHandlerError",
      scrubbedMessage: scrubMessage(error instanceof Error ? error.message : String(error)),
    },
    { connectorSystemName: CHIPS_CONNECTOR, reason: `action-chip-${chip.id}` },
  );
  emitToast({ title: `${chip.label} could not be completed.`, intent: "error" });
}

/** Location pin for the context line. */
function PinIcon() {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M10 2.5c3 0 5 2.2 5 5 0 3.5-5 10-5 10S5 11 5 7.5c0-2.8 2-5 5-5z" />
      <circle cx="10" cy="7.5" r="1.8" />
    </svg>
  );
}

/**
 * Selection-aware action chips, docked directly above the chat composer.
 *
 * Lives under `integrations/deckorate/features` even though it renders inside
 * the chat panel: every action behind a chip is a Deckorate/PowerPoint one, so
 * the chat panel's only tie to it is the single import in ChatTabPanel.
 *
 * The chip set follows what the user has selected in PowerPoint (nothing, a
 * text box, a table, a chart, a picture, or several shapes). The first
 * {@link VISIBLE_CHIP_COUNT} chips fill the tray; the remainder open from the
 * "···" button, so the dock never grows past one row in the narrow task pane.
 *
 * PowerPoint only — this add-in is shared with Word and Outlook, where none of
 * these actions exist. The caller gates on host (see ChatTabPanel) and the
 * selection hook independently no-ops off PowerPoint, so a wiring mistake
 * degrades to a static slide-chip tray rather than a broken pane.
 */
export function ActionChipDock({ onChipSelect }: Readonly<ActionChipDockProps>) {
  const classifyImage = useImageClassifier();
  const { kind, shapeCount, bannerLabel } = usePptSelectionKind(classifyImage);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const trayRef = useRef<HTMLDivElement | null>(null);

  // Memoised so the two slices aren't reallocated on every unrelated re-render
  // (overflow open/close, parent re-render). `?? SELECTION_CHIPS.slide` is a
  // defensive floor: `kind` is typed, but an unmapped value arriving at runtime
  // would otherwise make `chips` undefined and throw on `.slice`, taking the
  // whole chat panel down over a cosmetic tray.
  const { visibleChips, overflowChips } = useMemo(() => {
    const chips = SELECTION_CHIPS[kind] ?? SELECTION_CHIPS.slide;
    return {
      visibleChips: chips.slice(0, VISIBLE_CHIP_COUNT),
      overflowChips: chips.slice(VISIBLE_CHIP_COUNT),
    };
  }, [kind]);

  // A changed selection means a changed chip set, so an open overflow menu is
  // now listing actions the user never asked for. Close it.
  useEffect(() => setOverflowOpen(false), [kind]);

  // Dismiss on outside click / Escape, matching the slash-command palette.
  useEffect(() => {
    if (!overflowOpen) return;

    const onPointerDown = (event: MouseEvent): void => {
      if (!trayRef.current?.contains(event.target as Node)) setOverflowOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setOverflowOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [overflowOpen]);

  const handleChipClick = useCallback(
    (chip: ActionChip) => {
      setOverflowOpen(false);
      if (!onChipSelect) {
        emitToast({ title: `${chip.label} is not available yet.`, intent: "info" });
        return;
      }
      // The handler is caller-supplied and may be async, so a rejection has to be
      // caught here: left to float it becomes an unhandled rejection that tells
      // the user nothing and leaves no trace. Report it and keep the pane alive.
      try {
        const result = onChipSelect(chip);
        if (result instanceof Promise) result.catch((error: unknown) => reportChipFailure(chip, error));
      } catch (error) {
        // A handler that throws synchronously, before returning a promise.
        reportChipFailure(chip, error);
      }
    },
    [onChipSelect],
  );

  const renderChip = (chip: ActionChip) => (
    <button
      key={chip.id}
      type="button"
      className={styles.chip}
      data-chip-action={chip.id}
      data-testid={`action-chip-${chip.id}`}
      onClick={() => handleChipClick(chip)}
    >
      <span className={styles.chipIcon}>
        <chip.Icon />
      </span>
      {chip.label}
    </button>
  );

  return (
    <div className={styles.dock}>
      {/* The label is its own element rather than a bare text node beside the
          icon: `.contextLine` is a flex row whose `gap` provides the spacing, so
          a text node would leave whether a literal space belongs there ambiguous
          (typescript:S6772) — and adding one would render as a second gap. */}
      <p className={styles.contextLine} aria-live="polite">
        <span className={styles.contextIcon}>
          <PinIcon />
        </span>
        <span>
          {/* The classifier returns ready-to-render banner text (e.g. "Chart image
              — identified by AI vision"); it is preferred when present so the
              user can tell an AI-identified picture from a plain one, exactly as
              the mockup does. Falls back to the local label otherwise. */}
          Current slide · <strong>{bannerLabel ?? selectionLabel(kind, shapeCount)}</strong>
        </span>
      </p>

      <div className={styles.tray} ref={trayRef}>
        {visibleChips.map(renderChip)}

        {overflowChips.length > 0 && (
          <button
            type="button"
            className={`${styles.chip} ${styles.moreChip}`}
            aria-expanded={overflowOpen}
            aria-label={`More actions (${overflowChips.length})`}
            data-testid="action-chip-more"
            onClick={() => setOverflowOpen((open) => !open)}
          >
            ···
          </button>
        )}

        {overflowOpen && overflowChips.length > 0 && (
          <div className={styles.overflowMenu}>{overflowChips.map(renderChip)}</div>
        )}
      </div>
    </div>
  );
}
